import React, { useEffect, useState, useContext } from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass, faTrash, faHandshake, faPenToSquare, faUserEdit } from '@fortawesome/free-solid-svg-icons'
import { Formik, Form, Field } from 'formik'
import CustomLoader from '../layouts/CustomLoader'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import AuthContext from '../../middlewares/AuthContext'
import usePaginationComponentOptions from '../layouts/paginationComponentOptions'
import { useTranslation } from 'react-i18next'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface PartnerUser {
  id   : number
  email: string
}

interface Partner {
  id                    : number
  name                  : string
  description           : string
  street_address        : string
  address_line_2        : string
  zip_code              : string
  city                  : string
  state                 : string
  country               : string
  invoice_street_address: string
  invoice_address_line_2: string
  invoice_zip_code      : string
  invoice_city          : string
  invoice_state         : string
  invoice_country       : string
  invoice_email         : string
  discount              : number
  discount_smp          : number
  Users                 : PartnerUser[]
}

const pill = (bg: string): React.CSSProperties => ({
  display        : 'inline-flex',
  alignItems     : 'center',
  gap            : '0.3rem',
  padding        : '0.28rem 0.65rem',
  borderRadius   : 9999,
  backgroundColor: bg,
  color          : '#fff',
  fontWeight     : 700,
  fontSize       : '0.75rem',
  border         : 'none',
  cursor         : 'pointer',
  boxShadow      : `0 2px 6px ${bg}44`,
  textDecoration : 'none',
  whiteSpace     : 'nowrap' as const,
})

const addrLine = (v: string | null | undefined) =>
  v ? <li className="small text-capitalize" style={{ listStyle: 'none', padding: '1px 0' }}>{v}</li> : null

const ExpandedComponent: React.FC<{ data: Partner }> = ({ data: p }) => (
  <div style={{
    display            : 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    fontSize           : '0.82rem',
  }}>
    {/* Physical address */}
    <div style={{ padding: '0.75rem 1rem', borderRight: '1px solid #f1f5f9' }}>
      <p style={{ margin: '0 0 0.5rem', fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase' }}>
        {/* t passed through props would require prop drilling — use inline label */}
        Address
      </p>
      <ul style={{ padding: 0, margin: 0 }}>
        {addrLine(p.street_address)}
        {addrLine(p.address_line_2)}
        {addrLine(p.zip_code)}
        {addrLine(p.city)}
        {addrLine(p.state)}
        {addrLine(p.country)}
      </ul>
    </div>

    {/* Invoice address */}
    <div style={{ padding: '0.75rem 1rem', borderRight: '1px solid #f1f5f9' }}>
      <p style={{ margin: '0 0 0.5rem', fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase' }}>
        Invoice address
      </p>
      <ul style={{ padding: 0, margin: 0 }}>
        {addrLine(p.invoice_street_address)}
        {addrLine(p.invoice_address_line_2)}
        {addrLine(p.invoice_zip_code)}
        {addrLine(p.invoice_city)}
        {addrLine(p.invoice_state)}
        {addrLine(p.invoice_country)}
        {addrLine(p.invoice_email)}
      </ul>
    </div>

    {/* Users */}
    <div style={{ padding: '0.75rem 1rem' }}>
      <p style={{ margin: '0 0 0.5rem', fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase' }}>
        Users
      </p>
      {p.Users.length > 0
        ? <ul style={{ padding: 0, margin: 0 }}>
            {p.Users.map((u, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.3rem', listStyle: 'none' }}>
                <span className="small">{u.email}</span>
                <Link to={`/users/edit/${u.id}`} style={pill(BRAND)}>
                  <FontAwesomeIcon icon={faUserEdit} />
                </Link>
              </li>
            ))}
          </ul>
        : <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0 }}>No users found</p>
      }
    </div>
  </div>
)

const Partners: React.FC = () => {
  const { getHeaders }             = useContext(AuthContext) as AuthCtx
  const { t }                      = useTranslation()
  const paginationComponentOptions = usePaginationComponentOptions()

  const [data, setData]           = useState<Partner[]>([])
  const [loading, setLoading]     = useState(false)
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage]     = useState(10)
  const [search, setSearch]       = useState('')
  const [searchOpen, setSearchOpen]             = useState(false)
  const [resetPaginationToggle, setResetToggle] = useState(false)

  const fetchPartners = async (
    page      : number,
    perPageVal = perPage,
    searchVal  = search
  ) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.get<{ message: Partner[]; total: number }>(
        `${url}partners?page=${page}&per_page=${perPageVal}&search=${searchVal}`,
        { headers }
      )
      setData(res.data.message)
      setTotalRows(res.data.total)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange    = (page: number) => fetchPartners(page)
  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    fetchPartners(page, newPerPage)
  }
  const handleSearch = (values: { search: string }) => {
    setSearch(values.search)
    fetchPartners(1, perPage, values.search)
  }

  const deletePartner = async (id: number) => {
    const result = await Swal.fire({
      title            : t('partners.delete_title') || 'Delete Partner',
      text             : t('partners.delete_txt')   || 'Do you want to delete this partner?',
      icon             : 'warning',
      showCancelButton : true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor : '#94a3b8',
      confirmButtonText: t('partners.deleted') || 'Delete',
    })
    if (!result.isConfirmed) return
    try {
      const headers = await getHeaders()
      const res     = await axios.delete<{ response: string }>(
        `${url}partners/destroy/${id}`, { headers }
      )
      Swal.fire({ icon: 'success', title: res.data.response })
      fetchPartners(1)
      setResetToggle(p => !p)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  useEffect(() => { fetchPartners(1) }, [])

  const columns = [
    {
      name: t('partners.name'),
      cell: (row: Partner) => (
        <div>
          <p style={{ margin: 0, fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }} className="text-capitalize">{row.name}</p>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.78rem' }} className="text-capitalize">{row.description}</p>
        </div>
      ),
    },
    {
      name    : t('partners.products_discount'),
      maxWidth: '130px',
      cell    : (row: Partner) => (
        <span style={{
          display: 'inline-block', padding: '2px 10px', borderRadius: 9999,
          backgroundColor: `${BRAND}18`, color: BRAND_DARK, fontWeight: 700, fontSize: '0.82rem',
        }}>
          {row.discount}%
        </span>
      ),
    },
    {
      name    : 'Descuento SMP',
      maxWidth: '130px',
      cell    : (row: Partner) => (
        <span style={{
          display: 'inline-block', padding: '2px 10px', borderRadius: 9999,
          backgroundColor: '#eff6ff', color: '#1d4ed8', fontWeight: 700, fontSize: '0.82rem',
        }}>
          {row.discount_smp}%
        </span>
      ),
    },
    {
      name    : t('partners.actions'),
      minWidth: '180px',
      cell    : (row: Partner) => (
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', padding: '4px 0' }}>
          <Link to={`edit/${row.id}`} style={pill(BRAND)}>
            <FontAwesomeIcon icon={faPenToSquare} />
            <span>{t('partners.edit')}</span>
          </Link>
          <button onClick={() => deletePartner(row.id)} style={pill('#ef4444')}>
            <FontAwesomeIcon icon={faTrash} />
            <span>{t('partners.deleted')}</span>
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="row align-items-start">

      {/* Header label */}
      <div className="col-sm-4 col-md-2 mb-3 my-1">
        <span className="text-capitalize fw-bold">{t('partners.options') || 'Partners'}</span>
        <p className="text-muted small mt-1">Partner accounts</p>
      </div>

      {/* Search panel */}
      <div className="col-sm-8 col-md-10 mb-3">
        <div style={{ borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <button
            type="button"
            onClick={() => setSearchOpen(p => !p)}
            style={{
              width      : '100%',
              display    : 'flex',
              alignItems : 'center',
              gap        : '0.5rem',
              padding    : '0.75rem 1.25rem',
              background : searchOpen ? '#f8fafc' : '#fff',
              border     : 'none',
              borderBottom: searchOpen ? '1px solid #e2e8f0' : 'none',
              cursor     : 'pointer',
              fontWeight : 700,
              fontSize   : '0.9rem',
              color      : '#475569',
              textAlign  : 'left',
            }}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <span>{t('utilities.search')}</span>
            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#94a3b8', transform: searchOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
          </button>
          {searchOpen && (
            <div style={{ padding: '1.25rem' }}>
              <Formik initialValues={{ search }} onSubmit={handleSearch} enableReinitialize>
                <Form>
                  <div className="row align-items-center mb-3">
                    <label htmlFor="search" className="col-sm-2 col-form-label col-form-label-sm">{t('partners.search')}</label>
                    <div className="col-sm-10">
                      <Field type="text" className="form-control form-control-sm" name="search" id="search" placeholder={t('partners.search')} />
                    </div>
                  </div>
                  <button className="btn btn-sm btn-bd-primary" type="submit">
                    <span className="fw-bold">{t('utilities.search_btn')}</span>
                  </button>
                </Form>
              </Formik>
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="col-md-12 mb-3">
        <Link
          to="create"
          style={{
            display       : 'inline-flex',
            alignItems    : 'center',
            gap           : '0.45rem',
            padding       : '0.5rem 1.25rem',
            borderRadius  : 9999,
            background    : `linear-gradient(135deg, ${BRAND_DARK}, ${BRAND})`,
            color         : '#fff',
            fontWeight    : 700,
            fontSize      : '0.875rem',
            textDecoration: 'none',
            boxShadow     : `0 4px 14px ${BRAND}55`,
          }}
        >
          <FontAwesomeIcon icon={faHandshake} />
          <span>{t('partners.add_partner')}</span>
        </Link>
      </div>

      {/* DataTable */}
      <div className="col-md-12">
        <div className="card card-shadow">
          <DataTable
            columns={columns}
            data={data}
            fixedHeader
            fixedHeaderScrollHeight="600px"
            progressPending={loading}
            paginationResetDefaultPage={resetPaginationToggle}
            pagination
            paginationPerPage={perPage}
            paginationServer
            paginationTotalRows={totalRows}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            paginationComponentOptions={paginationComponentOptions}
            paginationRowsPerPageOptions={[10, 20, 50, 100]}
            progressComponent={<CustomLoader />}
            dense
            expandableRows
            expandableRowsComponent={ExpandedComponent}
          />
        </div>
      </div>

    </div>
  )
}

export default Partners
